import { computed, inject, ref, unref } from 'vue'
import { useQuizRestart } from './useQuizRestart'
import { htmlToPlainText } from '../utils/sanitizeHtml'

/**
 * Shared presentation + actions for a Transform lesson "card surface".
 *
 * Both the grid lesson card (TransformLessonCard) and the full-width continue card
 * (TransformContinueCard) render the same enriched lesson shape (from the store's
 * buildCategoryDetail) and need the same type/status derivation, plain-text description,
 * and navigate/restart behaviour. This composable is the single source for that logic so
 * the two surfaces cannot drift; each card keeps only its own copy (titles, meta strings,
 * CTA wording, badges) local.
 *
 * Must be called from a component setup (it uses inject for navigation).
 *
 * @param {() => Object|null} lessonGetter - returns the enriched lesson (ref or getter).
 * @param {Object} [options]
 * @param {() => Object|null} [options.moduleGetter] - when provided, navigation includes the
 *   resolved moduleTitle (continue-card behaviour); otherwise only the lesson's moduleId is
 *   sent (grid-card behaviour).
 */
export function useTransformLessonCard(lessonGetter, { moduleGetter = null } = {}) {
  const customNavigation = inject('customNavigation')
  const { restartQuiz } = useQuizRestart()
  const isRestarting = ref(false)

  const lesson = computed(() => unref(lessonGetter?.()))

  const isVideo = computed(() => lesson.value?.lessonType === 'video')
  const isQuiz = computed(() => lesson.value?.lessonType === 'quiz')
  const isWatched = computed(() => isVideo.value && lesson.value?.status === 'watched')
  const isInProgress = computed(() => lesson.value?.status === 'in_progress')
  const isLocked = computed(() => isQuiz.value && lesson.value?.quiz?.locked === true)
  const isPassed = computed(() => isQuiz.value && lesson.value?.status === 'passed')
  const isFailed = computed(() => isQuiz.value && lesson.value?.status === 'failed')
  const isPassedOrFailed = computed(() => isPassed.value || isFailed.value)

  const questionCount = computed(() => lesson.value?.quiz?.questions ?? 0)
  const questionLabel = computed(
    () => `${questionCount.value} Question${questionCount.value !== 1 ? 's' : ''}`
  )

  const plainDescription = computed(() =>
    htmlToPlainText(lesson.value?.bodyHtml || lesson.value?.descriptionHtml || '')
  )

  function navigateToLesson(lessonId, lessonTitle) {
    if (!lessonId) return
    const module = moduleGetter ? unref(moduleGetter()) : null
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformLessonDetail.name,
      params: {
        lessonId,
        lessonTitle: lessonTitle ?? '',
        moduleId: module?.id ?? lesson.value?.moduleId,
        ...(moduleGetter && { moduleTitle: module?.title ?? '' }),
      },
    })
  }

  // Navigate to the lesson. For a passed/failed quiz this is a retake, so reset the
  // persisted + local progress first; navigation proceeds even if the reset fails (the
  // player can still retake locally).
  async function goToLesson() {
    if (isQuiz.value && isPassedOrFailed.value) {
      isRestarting.value = true
      try {
        await restartQuiz({ lessonId: lesson.value?.id, courseId: lesson.value?.courseId })
      } finally {
        isRestarting.value = false
      }
    }
    navigateToLesson(lesson.value?.id, lesson.value?.title)
  }

  // Jump to the prerequisite video lesson that unlocks a locked quiz.
  function goToRequiredLesson() {
    navigateToLesson(lesson.value?.quiz?.requiredLessonId, null)
  }

  return {
    isVideo,
    isQuiz,
    isWatched,
    isInProgress,
    isLocked,
    isPassed,
    isFailed,
    isPassedOrFailed,
    questionCount,
    questionLabel,
    plainDescription,
    isRestarting,
    goToLesson,
    goToRequiredLesson,
  }
}
